import React from 'react';
import { BarChart2, Info } from 'lucide-react';
import { calculateVAT } from '../../../../../utils/calculations/finance';
import { formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент сравнительной таблицы для различных ставок НДС
 */
const ComparisonTable = ({ amount, calculationType, selectedRate, customRate }) => {
  // Стандартные ставки НДС для сравнения
  const standardRates = [
    { id: '20', name: 'НДС 20%', value: 20 },
    { id: '10', name: 'НДС 10%', value: 10 },
    { id: '0', name: 'НДС 0%', value: 0 }
  ];

  // Добавляем пользовательскую ставку, если она есть
  const rates = [...standardRates];
  if (selectedRate === 'custom' && customRate) {
    const customRateValue = parseFloat(customRate);
    if (!isNaN(customRateValue) && customRateValue >= 0) {
      rates.push({
        id: 'custom',
        name: `НДС ${customRateValue}%`,
        value: customRateValue
      });
    }
  }

  // Рассчитываем результаты для каждой ставки
  const results = rates.map(rate => {
    const result = calculateVAT(
      amount, 
      rate.value, 
      calculationType === 'extract' // includesVat: true для выделения, false для начисления
    );

    return {
      rate: rate,
      result: result,
      isSelected: selectedRate === rate.id || (selectedRate === 'none' && rate.value === 0)
    };
  });

  // Сортируем результаты по ставке
  results.sort((a, b) => b.rate.value - a.rate.value);

  // Функция для определения класса строки (выделяем выбранную ставку)
  const getRowClass = (isSelected) => {
    return isSelected
      ? 'bg-green-50 dark:bg-green-900/20 font-medium'
      : 'hover:bg-gray-50 dark:hover:bg-gray-700/50';
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mb-8">
      <div className="mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <BarChart2 className="w-5 h-5 mr-2 text-green-500" />
          Сравнение по ставкам НДС
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Сравнение результатов расчета с различными ставками НДС
        </p>
      </div>

      {/* Информационная подсказка */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded p-3 mb-4 flex items-start">
        <Info className="text-blue-500 dark:text-blue-400 w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-700 dark:text-blue-300">
          <p>Таблица показывает, как изменяются суммы при разных ставках НДС. 
             Текущий расчет выделен зеленым цветом.</p>
        </div>
      </div>

      {/* Таблица сравнения */}
      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
        <table className="w-full text-sm text-gray-700 dark:text-gray-300 min-w-full">
          <thead className="text-xs text-gray-700 dark:text-gray-300 uppercase bg-gray-100 dark:bg-gray-700">
            <tr>
              <th scope="col" className="px-4 py-3 font-semibold text-left">Ставка НДС</th>
              <th scope="col" className="px-4 py-3 font-semibold text-right">Сумма без НДС</th>
              <th scope="col" className="px-4 py-3 font-semibold text-right">Сумма НДС</th>
              <th scope="col" className="px-4 py-3 font-semibold text-right">Сумма с НДС</th>
              <th scope="col" className="px-4 py-3 font-semibold text-right">% от суммы с НДС</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {results.map((item, index) => (
              <tr 
                key={item.rate.id} 
                className={`${getRowClass(item.isSelected)} transition-colors duration-100`}
              >
                <td className="px-4 py-3 whitespace-nowrap">
                  {item.rate.name}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-right">
                  {formatCurrency(item.result.amountWithoutVAT, 2)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-right">
                  {formatCurrency(item.result.vatAmount, 2)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-right font-medium">
                  {formatCurrency(item.result.amountWithVAT, 2)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-right">
                  {(item.result.vatAmount / item.result.amountWithVAT * 100).toFixed(2)}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 text-xs text-gray-500 dark:text-gray-400">
        * Результаты округлены до 2 знаков после запятой для наглядности.
      </div>
    </div>
  );
};

export default ComparisonTable;